import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send, AlertCircle } from 'lucide-react';
import { useJobTypes, useCreateJob } from '@/hooks/useJobs';
import type { CreateJobPayload } from '@/lib/types';

interface SubmitJobModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SubmitJobModal({ isOpen, onClose }: SubmitJobModalProps) {
  const navigate = useNavigate();
  const { data: jobTypesData, isLoading: loadingTypes } = useJobTypes();
  const createJobMutation = useCreateJob();

  const [formData, setFormData] = useState<CreateJobPayload>({
    type_name: '',
    payload: undefined,
    priority: 1,
    max_retries: 3,
    callback_url: '',
  });

  const [payloadText, setPayloadText] = useState('');
  const [payloadError, setPayloadError] = useState<string | null>(null);

  if (!isOpen) return null;

  const jobTypes = jobTypesData?.items || [];

  const handlePayloadChange = (value: string) => {
    setPayloadText(value);
    setPayloadError(null);

    // Try to parse JSON
    if (value.trim()) {
      try {
        JSON.parse(value);
      } catch {
        setPayloadError('Invalid JSON format');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Parse payload JSON
    let parsedPayload: Record<string, any> | undefined = undefined;
    if (payloadText.trim()) {
      try {
        parsedPayload = JSON.parse(payloadText);
      } catch {
        setPayloadError('Invalid JSON format');
        return;
      }
    }

    // Build submission payload
    const submitPayload: CreateJobPayload = {
      type_name: formData.type_name,
      priority: formData.priority,
      max_retries: formData.max_retries,
    };

    if (parsedPayload) {
      submitPayload.payload = parsedPayload;
    }

    if (formData.callback_url?.trim()) {
      submitPayload.callback_url = formData.callback_url.trim();
    }

    try {
      const newJob = await createJobMutation.mutateAsync(submitPayload);

      // Close modal and navigate to new job
      onClose();
      navigate(`/jobs/${newJob.id}`);
    } catch (error) {
      console.error('Failed to create job:', error);
    }
  };

  const handleClear = () => {
    setFormData({
      type_name: '',
      payload: undefined,
      priority: 1,
      max_retries: 3,
      callback_url: '',
    });
    setPayloadText('');
    setPayloadError(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onClose();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] m-4 glass-card rounded-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold">Submit New Job</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Create a job for AI testing and experimentation
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Job Type */}
          <div className="space-y-2">
            <label htmlFor="job-type" className="text-sm font-medium">
              Job Type <span className="text-red-400">*</span>
            </label>
            {loadingTypes ? (
              <div className="text-sm text-muted-foreground">Loading job types...</div>
            ) : (
              <select
                id="job-type"
                required
                value={formData.type_name}
                onChange={(e) => setFormData({ ...formData, type_name: e.target.value })}
                className="w-full px-3 py-2 rounded-md bg-muted/50 border border-border focus:border-primary focus:outline-none transition-colors"
              >
                <option value="">Select job type...</option>
                {jobTypes.map((type) => (
                  <option key={type.name} value={type.name}>
                    {type.name} {type.description && `- ${type.description}`}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Priority and Max Retries */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="priority" className="text-sm font-medium">Priority (1-5)</label>
              <input
                id="priority"
                type="number"
                min="1"
                max="5"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value, 10) })}
                className="w-full px-3 py-2 rounded-md bg-muted/50 border border-border focus:border-primary focus:outline-none transition-colors"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="max-retries" className="text-sm font-medium">Max Retries</label>
              <input
                id="max-retries"
                type="number"
                min="0"
                value={formData.max_retries}
                onChange={(e) => setFormData({ ...formData, max_retries: parseInt(e.target.value, 10) })}
                className="w-full px-3 py-2 rounded-md bg-muted/50 border border-border focus:border-primary focus:outline-none transition-colors"
              />
            </div>
          </div>

          {/* Callback URL */}
          <div className="space-y-2">
            <label htmlFor="callback-url" className="text-sm font-medium">Callback URL (optional)</label>
            <input
              id="callback-url"
              type="url"
              placeholder="https://example.com/webhook"
              value={formData.callback_url}
              onChange={(e) => setFormData({ ...formData, callback_url: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-muted/50 border border-border focus:border-primary focus:outline-none transition-colors"
            />
          </div>

          {/* Payload */}
          <div className="space-y-2">
            <label htmlFor="payload" className="text-sm font-medium">Payload (JSON)</label>
            <div className="relative">
              <textarea
                id="payload"
                value={payloadText}
                onChange={(e) => handlePayloadChange(e.target.value)}
                placeholder={'{\n  "workflow": "your-workflow-name",\n  "inputs": {\n    "param1": "value1"\n  }\n}'}
                rows={12}
                className={`w-full px-3 py-2 rounded-md bg-muted/50 border ${
                  payloadError ? 'border-red-500' : 'border-border'
                } focus:border-primary focus:outline-none transition-colors font-mono text-sm`}
              />
              {payloadError && (
                <div className="absolute top-2 right-2 flex items-center gap-2 px-2 py-1 rounded bg-red-500/20 border border-red-500/30">
                  <AlertCircle className="h-4 w-4 text-red-400" />
                  <span className="text-xs text-red-400">{payloadError}</span>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Job-specific configuration and parameters in JSON format
            </p>
          </div>

          {/* Error Display */}
          {createJobMutation.isError && (
            <div className="p-4 rounded-md bg-red-500/10 border border-red-500/30">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-red-400">Failed to create job</div>
                  <div className="text-sm text-red-400/80 mt-1">
                    {createJobMutation.error instanceof Error
                      ? createJobMutation.error.message
                      : 'Unknown error occurred'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={handleClear}
              className="px-4 py-2 rounded-md hover:bg-muted transition-colors text-sm font-medium"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md hover:bg-muted transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.type_name || !!payloadError || createJobMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-4 w-4" />
              <span>{createJobMutation.isPending ? 'Submitting...' : 'Submit Job'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
